import { useCallback, useState, useEffect, useRef } from 'react';
import { useHotelQuestionnaireContext } from '../contexts/HotelQuestionnaireContext';
import { useToast } from './useToast';
import { useSplash } from '../contexts/SplashContext';
import { useQuestionRefs } from '../contexts/QuestionRefsContext';

/**
 * Hook for managing hotel questionnaire steps
 */
export const useHotelQuestionnaireSteps = () => {
  const { state, actions } = useHotelQuestionnaireContext();

  const getCurrentStepIndex = useCallback(() => {
    if (!state.stepsData) return 0;
    return state.stepsData.steps.findIndex(
      step => step.key === state.stepsData?.currentStepKey
    );
  }, [state.stepsData]);

  const isLastStep = useCallback(() => {
    if (!state.stepsData) return false;
    return getCurrentStepIndex() === state.stepsData.steps.length - 1;
  }, [state.stepsData, getCurrentStepIndex]);

  const canGoToPreviousStep = useCallback(() => {
    return getCurrentStepIndex() > 0;
  }, [getCurrentStepIndex]);

  const handlePreviousStep = useCallback(() => {
    if (!state.stepsData || !canGoToPreviousStep()) return;
    const currentIndex = getCurrentStepIndex();
    const previousStep = state.stepsData.steps[currentIndex - 1];
    if (previousStep) {
      // For previous step, check if there are draft changes
      if (actions.hasDraftChanges()) {
        // You might want to implement specific logic for previous step navigation
        // For now, we'll proceed without confirmation for backward navigation
        actions.clearDraftAnswers();
      }
      actions.goToStep(previousStep.key);
    }
  }, [state.stepsData, canGoToPreviousStep, getCurrentStepIndex, actions]);

  const handleNextStep = useCallback(async () => {
    if (!state.stepsData) return;
    const currentIndex = getCurrentStepIndex();
    const nextStep = state.stepsData.steps[currentIndex + 1];
    if (nextStep) {
      // For next step, we should probably auto-save draft changes
      if (actions.hasDraftChanges()) {
        try {
          // Get combined answers (main + draft)
          const combinedAnswers = { ...state.answers, ...state.draftAnswers };

          // Import isQuestionnaireComplete here to avoid circular dependencies
          const { isQuestionnaireComplete } = await import(
            '../utils/questionnaireUtils'
          );

          // Check if questionnaire is complete
          const isComplete = isQuestionnaireComplete(
            state.questionnaireData,
            combinedAnswers
          );
          const isDraft = !isComplete;

          console.log('Next step save:', {
            isComplete,
            isDraft,
            combinedAnswers,
          });

          // Save as draft if incomplete, otherwise save normally
          await actions.saveAnswers(isDraft);
        } catch (error) {
          console.error('Failed to save before moving to next step:', error);
          return; // Don't proceed if save fails
        }
      }
      await actions.goToStep(nextStep.key);
    }
  }, [
    state.stepsData,
    getCurrentStepIndex,
    actions,
    state.answers,
    state.draftAnswers,
    state.questionnaireData,
  ]);

  return {
    stepsData: state.stepsData,
    stepsLoading: state.stepsLoading,
    stepsError: state.stepsError,
    isInitialLoading: state.stepsLoading === 'initial',
    isSaving: state.stepsLoading === 'saving',
    isSwitchingType: state.isSwitchingType,
    currentStepKey: state.stepsData?.currentStepKey,
    getCurrentStepIndex,
    isLastStep,
    canGoToPreviousStep,
    handlePreviousStep,
    handleNextStep,
    goToStep: actions.goToStep,
    goToSourcesWithGroup: actions.goToSourcesWithGroup,
    fetchSteps: actions.fetchSteps,
    refreshSteps: actions.refreshSteps,
  };
};

/**
 * Hook for managing questionnaire data and loading states
 */
export const useHotelQuestionnaireData = () => {
  const { state, actions } = useHotelQuestionnaireContext();

  return {
    questionnaireData: state.questionnaireData,
    questionnaireLoading: state.questionnaireLoading,
    questionnaireError: state.questionnaireError,
    fetchQuestionnaire: actions.fetchQuestionnaire,
    submitQuestionnaire: actions.submitQuestionnaire,
  };
};

/**
 * Hook for managing hotel questionnaire answers
 */
export const useHotelQuestionnaireAnswers = () => {
  const { state, actions } = useHotelQuestionnaireContext();

  // console.log("Current Answers State ::", state.answers);
  // console.log("Current Draft Answers State ::", state.draftAnswers);

  const handleSaveAndNext = useCallback(async () => {
    try {
      const result = await actions.saveAnswers(false);
      return result;
    } catch (error) {
      console.error('Failed to save and proceed:', error);
      throw error;
    }
  }, [actions]);

  const handleSaveDraft = useCallback(async () => {
    try {
      const result = await actions.saveAnswers(true);
      return result;
    } catch (error) {
      console.error('Failed to save draft:', error);
      throw error;
    }
  }, [actions]);

  const handleSubmitFinal = useCallback(async () => {
    try {
      const result = await actions.saveAnswers(false);
      if (result?.success) {
        console.log('Application submitted successfully!');
      }
      return result;
    } catch (error) {
      console.error('Failed to submit application:', error);
      throw error;
    }
  }, [actions]);

  // Get combined answers (main + draft)
  const getCombinedAnswers = useCallback(() => {
    return { ...state.answers, ...state.draftAnswers };
  }, [state.answers, state.draftAnswers]);

  // Check if current questionnaire is complete with draft answers
  const isCurrentQuestionnaireComplete = useCallback(async () => {
    if (!state.questionnaireData) return false;

    const combinedAnswers = getCombinedAnswers();

    // Import isQuestionnaireComplete here to avoid circular dependencies
    const { isQuestionnaireComplete } = await import(
      '../utils/questionnaireUtils'
    );

    return isQuestionnaireComplete(state.questionnaireData, combinedAnswers);
  }, [state.questionnaireData, getCombinedAnswers]);

  // Smart save function that determines draft vs final based on completion
  const handleSmartSave = useCallback(async () => {
    try {
      const isComplete = await isCurrentQuestionnaireComplete();
      const isDraft = !isComplete;
      const result = await actions.saveAnswers(isDraft);
      return { result, isDraft };
    } catch (error) {
      console.error('Failed to save:', error);
      throw error;
    }
  }, [actions, isCurrentQuestionnaireComplete]);

  return {
    answers: state.answers,
    draftAnswers: state.draftAnswers,
    combinedAnswers: getCombinedAnswers(),
    sources: state.sources,
    updateAnswer: actions.updateAnswer, // Context already handles dependencies
    updateDraftAnswer: actions.updateDraftAnswer, // Context already handles dependencies
    applyDraftAnswers: actions.applyDraftAnswers,
    clearDraftAnswers: actions.clearDraftAnswers,
    hasDraftChanges: actions.hasDraftChanges,
    saveAnswers: actions.saveAnswers,
    isCurrentQuestionnaireComplete,
    handleSmartSave,
    handleSaveAndNext,
    handleSaveDraft,
    handleSubmitFinal,
    updateQuestionExclusionStates: actions.updateQuestionExclusionStates,
  };
};

/**
 * Hook for managing sources data
 */
export const useHotelSources = () => {
  const { state, actions } = useHotelQuestionnaireContext();
  const { error, warning } = useToast();
  const [approvedCount, setApprovedCount] = useState(0);
  console.log("Sources Loading??:", state.sourcesLoading)

  // Track review changes - updates draft reviews
  const handleReviewChange = useCallback(
    (questionId: string, isApproved: boolean | null, rejectionReasonId?: string | null) => {
      console.log('Review change:', { questionId, isApproved, rejectionReasonId });
      actions.updateDraftReview(questionId, isApproved, rejectionReasonId);
    },
    [actions]
  );

  // Update approved count whenever reviews or draftReviews changes
  useEffect(() => {
    // Combine main reviews with draft reviews (draft takes precedence)
    const combinedReviews = { ...state.reviews, ...state.draftReviews };
    const count = Object.values(combinedReviews).filter(
      (review: any) => review.approved === true
    ).length;
    setApprovedCount(count);
  }, [state.reviews, state.draftReviews]);

  // Save sources review
  const saveSourcesReview = useCallback(async (showToast : boolean = true) => {
    console.log("Saving Sources for Review section")
    try {
      if (!state.hotelId) {
        throw new Error('Hotel ID is required');
      }
      // Combine main reviews with draft reviews
      const combinedReviews = { ...state.reviews, ...state.draftReviews };
      console.log("Combined Reviews before save:", combinedReviews);

      if (Object.keys(combinedReviews).length === 0) {
        showToast && error('No reviews to save.');
        return null;
      }

      const response = await actions.saveReviews();
      return response; // Return the response with hasMissingAttachments flag
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save sources review';
      error(errorMessage);
      console.error('Failed to save sources review:', err);
      throw err;
    }
  }, [state.hotelId, state.reviews, state.draftReviews, actions, error]);

  // Compute combined reviews (draft takes precedence over main)
  const getCombinedReviews = useCallback(() => {
    return { ...state.reviews, ...state.draftReviews };
  }, [state.reviews, state.draftReviews]);

  return {
    sourcesData: state.sourcesData,
    sourcesLoading: state.sourcesLoading,
    sourcesError: state.sourcesError,
    fetchSources: actions.fetchSources,
    updateQuestionAttachments: actions.updateQuestionAttachments,
    updateQuestionUploadedFilenames: actions.updateQuestionUploadedFilenames,
    // Review tracking
    reviews: state.reviews,
    draftReviews: state.draftReviews,
    combinedReviews: getCombinedReviews(),
    approvedCount,
    handleReviewChange,
    saveSourcesReview,
    hasDraftReviews: actions.hasDraftReviews,
  };
};

/**
 * Main hook that combines all questionnaire functionality
 * Use this when you need access to all questionnaire features
 */
export const useHotelQuestionnaire = (hotelId?: string) => {
  const { state, actions } = useHotelQuestionnaireContext();
  const steps = useHotelQuestionnaireSteps();
  const data = useHotelQuestionnaireData();
  const answers = useHotelQuestionnaireAnswers();

  // Set hotel ID if provided
  if (hotelId && hotelId !== state.hotelId) {
    actions.setHotelId(hotelId);
  }

  return {
    // State
    hotelId: state.hotelId,

    // Steps
    ...steps,

    // Data
    ...data,

    // Answers
    ...answers,

    // Actions
    resetState: actions.resetState,
  };
};

/**
 * Hook for managing navigation confirmation when there are unsaved changes
 */
export const useNavigationConfirmation = (options?: {
  scrollToQuestion?: (questionId: string) => void;
  setUnansweredQuestions?: (questionIds: string[], forValidation?: boolean) => void;
  setQuestionErrors?: (errors: Record<string, string>) => void;
}) => {
  const optionsRef = useRef({
  scrollToQuestion: undefined,
  setUnansweredQuestions: undefined,
  setQuestionErrors: undefined,
});
  const { state, actions } = useHotelQuestionnaireContext();
  const { success, error } = useToast();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isConfirmingNavigation, setIsConfirmingNavigation] = useState(false);
  const [isSavingNavigation, setIsSavingNavigation] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<{
    stepKey: string;
    stepName?: string;
  } | null>(null);

useEffect(() => {
  if (options) {
    optionsRef.current = options;
  }
}, [options]);

  const requestNavigation = useCallback(
    (stepKey: string, stepName?: string) => {
      if (actions.hasDraftChanges()) {
        setPendingNavigation({ stepKey, stepName });
        setShowConfirmModal(true);
        return false; // Navigation blocked
      }
      return true; // Navigation allowed
    },
    [actions]
  );

  const cancelNavigation = useCallback(() => {
    setShowConfirmModal(false);
    setPendingNavigation(null);
    setIsConfirmingNavigation(false);
    setIsSavingNavigation(false);
  }, []);

  const confirmNavigation = useCallback(async () => {
    if (pendingNavigation) {
      setIsConfirmingNavigation(true);
      try {
        // Clear attachments for questions with invalid attachment options before discarding changes
        if (state.hotelId && state.questionnaireData && actions.hasDraftChanges()) {
          try {
            // Import the utility for clearing attachments
            const { clearAttachmentsForDraftAnswers } = await import('../utils/attachmentDeletionUtils');
            const { isStandardQuestionnaire, isGroupedQuestionnaire } = await import('../utils/questionnaireUtils');
            
            // Get questions based on questionnaire type
            let questions: any[] = [];
            if (isStandardQuestionnaire(state.questionnaireData)) {
              questions = state.questionnaireData.metadata.fields;
            } else if (isGroupedQuestionnaire(state.questionnaireData)) {
              questions = state.questionnaireData.groups.flatMap(group => group.metadata.fields);
            }
            
            // Clear attachments for ALL questions that have draft answers and attachment capability
            if (questions.length > 0 && state.draftAnswers) {
              console.log("Clearing attachments for all questions with draft answers during navigation");
              await clearAttachmentsForDraftAnswers(state.hotelId, questions, state.draftAnswers);
            }
          } catch (error) {
            console.error('Failed to clear attachments for draft answers during navigation:', error);
            // Continue with navigation even if attachment clearing fails
          }
        }
        
        actions.clearDraftAnswers();
        // Only navigate to step if it's not a close action
        if (pendingNavigation.stepKey !== 'close') {
          await actions.goToStep(pendingNavigation.stepKey);
        }
        setShowConfirmModal(false);
        setPendingNavigation(null);
      } catch (error) {
        console.error('Failed to navigate:', error);
      } finally {
        setIsConfirmingNavigation(false);
      }
    }
  }, [actions, pendingNavigation, state.hotelId, state.questionnaireData, state.draftAnswers]);

  const saveAndNavigate = useCallback(async () => {
    console.log('Save and navigate triggered');
    if (pendingNavigation) {
      setIsSavingNavigation(true);
      try {
        // Get combined answers (main + draft)
        const combinedAnswers = { ...state.answers, ...state.draftAnswers };

        // Import validation utilities
        const { 
          isQuestionnaireComplete, 
          areRequiredQuestionsAnswered,
          validateYourDetailsSection,
          validateHotelDetailsSection
        } = await import('../utils/questionnaireUtils');

        // Special handling for 'hotel-location' section
        if (state.stepsData?.currentStepKey === 'hotel-location') {
          const validation = validateHotelDetailsSection(
            state.questionnaireData,
            combinedAnswers
          );

          if (!validation.isValid) {
            const firstMissingField = validation.missingFields[0];

            const unAnsweredQuestions = validation.missingFields.map(f => f.id);
            if (options?.setUnansweredQuestions) {
              options.setUnansweredQuestions(unAnsweredQuestions, true);
            }

            // Scroll to first missing field
            if (unAnsweredQuestions.length > 0 && options?.scrollToQuestion) {
              setTimeout(() => {
                options.scrollToQuestion!(unAnsweredQuestions[0]);
              }, 100);
            }

            setShowConfirmModal(false);
            setIsSavingNavigation(false);
            error(`${firstMissingField.label} field is required`, { duration: 4000 });
            return;
          }
          console.log('Hotel location validation passed');

          // If validation passed, save and navigate
          try {
            await actions.saveAnswers(false); // Not a draft for hotel-location
            await actions.refreshSteps();
            success('Changes saved successfully!');

            if (pendingNavigation.stepKey !== 'close') {
              await actions.goToStep(pendingNavigation.stepKey);
            }

            setShowConfirmModal(false);
            setPendingNavigation(null);
            setIsSavingNavigation(false);
            return;
          } catch (err: any) {
            // Check if it's a validation error (400 status)
            if (err.response?.status === 400 && err.response?.data?.errors) {
              const validationErrors = err.response.data.errors;
              
              // Set error messages for each field
              if (optionsRef.current?.setQuestionErrors) {
                optionsRef.current.setQuestionErrors(validationErrors);
              }
              
              // Get the question IDs that have errors and scroll to first one
              const errorQuestionIds = Object.keys(validationErrors);
              if (errorQuestionIds.length > 0 && optionsRef.current?.scrollToQuestion) {
                setTimeout(() => {
                  optionsRef.current.scrollToQuestion!(errorQuestionIds[0]);
                }, 100);
              }
              
              // Close modal and show toast AFTER errors are applied (with small delay)
              setTimeout(() => {
                setShowConfirmModal(false);
                setIsSavingNavigation(false);
                error(
                  err.response.data.message || 'Please fix the validation errors'
                );
              }, 50);
              return;
            } else {
              setShowConfirmModal(false);
              setIsSavingNavigation(false);
              error(
                err.message || 'Failed to save hotel details. Please try again.'
              );
              return;
            }
          }
        }

        // Special handling for 'your-details' section
        if (state.stepsData?.currentStepKey === 'your-details') {
          const validation = validateYourDetailsSection(
            state.questionnaireData,
            combinedAnswers
          );

          if (!validation.isValid) {
            const firstMissingField = validation.missingFields[0];
            setShowConfirmModal(false);
            error(`${firstMissingField.label} field is required`, { duration: 4000 });
            return;
          }

          // If validation passed, save and navigate
          await actions.saveAnswers(false); // Not a draft for your-details
          await actions.refreshSteps();
          success('Changes saved successfully!');

          if (pendingNavigation.stepKey !== 'close') {
            await actions.goToStep(pendingNavigation.stepKey);
          }

          setShowConfirmModal(false);
          setPendingNavigation(null);
          setIsSavingNavigation(false);
          return;
        }

        // Original validation logic for other sections
        const isComplete = isQuestionnaireComplete(
          state.questionnaireData,
          combinedAnswers
        );
        const areRequiredAnswered = areRequiredQuestionsAnswered(
          state.questionnaireData,
          combinedAnswers
        );

        if (!areRequiredAnswered) {
          //close the modal and show error toast
          setShowConfirmModal(false);
          error('Please answer all required questions before saving.');
          return;
        }

        const isDraft = !isComplete;

        console.log('Navigation save:', {
          isComplete,
          isDraft,
          combinedAnswers,
        });

        // Save as draft if incomplete, otherwise save normally
        await actions.saveAnswers(isDraft);

        // Refresh steps to get updated progress
        await actions.refreshSteps();

        // Show success toast
        success('Changes saved successfully!');

        // Only navigate to step if it's not a close action
        if (pendingNavigation.stepKey !== 'close') {
          await actions.goToStep(pendingNavigation.stepKey);
        }

        setShowConfirmModal(false);
        setPendingNavigation(null);
      } catch (saveError) {
        console.error('Failed to save before navigation:', saveError);
        error('Failed to save changes. Please try again.');
        // Keep modal open on error
      } finally {
        setIsSavingNavigation(false);
      }
    }
  }, [
    actions,
    pendingNavigation,
    state.answers,
    state.draftAnswers,
    state.questionnaireData,
    state.stepsData?.currentStepKey,
    success,
    error,
    options,
  ]);

  return {
    showConfirmModal,
    pendingNavigation,
    isConfirmingNavigation,
    isSavingNavigation,
    requestNavigation,
    cancelNavigation,
    confirmNavigation,
    saveAndNavigate,
  };
};

/**
 * Hook for managing readonly questionnaire (full detailed assessment)
 */
export const useReadonlyQuestionnaire = () => {
  const { state, actions } = useHotelQuestionnaireContext();

  return {
    // State
    readonlyQuestionnaireData: state.readonlyQuestionnaireData,
    readonlyQuestionnaireLoading: state.readonlyQuestionnaireLoading,
    readonlyQuestionnaireError: state.readonlyQuestionnaireError,

    // Actions
    fetchReadonlyQuestionnaire: actions.fetchReadonlyQuestionnaire,
  };
};
